import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle, calculateHeaderGroupHeight } from '@theme/utilities';
import { getScrollContainer, getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;
  #scrollContainer = null;
  #scrollMarker = null;
  #naturalTop = 0;
  #headerHeight = 0;
  #lastScrollTop = 0;
  #directionStart = 0;
  #direction = 0;
  #scrollRafId = null;
  #productColumnsMediaQuery = window.matchMedia('(min-width: 750px)');
  #productTouchMediaQuery = window.matchMedia('(any-pointer: coarse)');

  #hasIndependentProductColumns = () => this.#productColumnsMediaQuery.matches
    && !this.#productTouchMediaQuery.matches
    && document.querySelector('.product-information--independent') !== null;

  #resizeObserver = new ResizeObserver(() => {
    const height = Math.round(this.getBoundingClientRect().height);
    const value = `${height}px`;
    if (document.body.style.getPropertyValue('--header-height') !== value) {
      document.body.style.setProperty('--header-height', value);
    }
    this.#measureScrollOrigin();
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #measureScrollOrigin = () => {
    if (!this.#scrollMarker) return;
    const root = getScrollContainer();
    const rootTop = root === document.scrollingElement || root === document.documentElement
      ? 0 : root.getBoundingClientRect().top + root.clientTop;
    this.#naturalTop = this.#scrollMarker.getBoundingClientRect().top + getScrollTop() - rootTop;
    this.#headerHeight = this.offsetHeight;
  };

  #readScrollTop() {
    const root = getScrollContainer();
    return Math.max(0, Math.min(getScrollTop(), Math.max(0, root.scrollHeight - root.clientHeight)));
  }

  #setScrollState(state, direction = 'none') {
    if (this.dataset.stickyState !== state) {
      this.dataset.stickyState = state;
      if (state === 'active' && this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
    }
    if (this.dataset.scrollDirection !== direction) this.dataset.scrollDirection = direction;
  }

  #resetScrollState = () => {
    this.#measureScrollOrigin();
    const top = this.#readScrollTop();
    this.#lastScrollTop = this.#directionStart = top;
    this.#direction = 0;
    if (this.querySelector('#Details-menu-drawer-container[open]')) return;
    if (this.#hasIndependentProductColumns()) {
      this.#setScrollState('active');
      return;
    }
    this.#setScrollState(this.getAttribute('sticky') === 'always'
      ? (top > this.#naturalTop + 1 ? 'active' : 'inactive')
      : (top > this.#naturalTop + this.#headerHeight ? 'idle' : 'inactive'));
  };

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      this.querySelector('overflow-list')?.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  #handleBreakpointChange = () => {
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = getScrollEventTarget();
    this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    this.#resetScrollState();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    if (this.#hasIndependentProductColumns()) {
      this.#setScrollState('active');
      return;
    }
    const top = this.#readScrollTop();
    const stickyMode = this.getAttribute('sticky');
    if (document.documentElement.hasAttribute('scroll-lock') || this.querySelector('#Details-menu-drawer-container[open]')) {
      this.#lastScrollTop = this.#directionStart = top;
      this.#direction = 0;
      return;
    }
    if (top <= this.#naturalTop + 1) {
      this.#lastScrollTop = this.#directionStart = top;
      this.#direction = 0;
      this.#setScrollState('inactive');
      return;
    }
    const delta = top - this.#lastScrollTop;
    if (Math.abs(delta) < 0.5) return;
    const direction = delta > 0 ? 1 : -1;
    if (direction !== this.#direction) {
      this.#direction = direction;
      this.#directionStart = this.#lastScrollTop;
    }
    this.#lastScrollTop = top;
    if (stickyMode === 'always') {
      this.#setScrollState('active', direction < 0 ? 'up' : 'down');
      return;
    }
    if (top <= this.#naturalTop + this.#headerHeight) {
      if (this.dataset.stickyState !== 'active' || (direction > 0 && top - this.#directionStart >= 12)) {
        this.#setScrollState('inactive');
      }
      return;
    }
    if (Math.abs(top - this.#directionStart) < 12) return;
    this.#setScrollState(direction < 0 ? 'active' : 'idle', direction < 0 ? 'up' : 'none');
  };
  #centerActiveCollectionLink = () => {
    const scrollContainer = this.querySelector(
      '.mutae-collection-nav__scroll'
    );

    const activeLink = scrollContainer?.querySelector(
      '.mutae-collection-nav__link[aria-current="page"]'
    );

    if (
      !(scrollContainer instanceof HTMLElement) ||
      !(activeLink instanceof HTMLElement)
    ) {
      return;
    }

    const maxScrollLeft =
      scrollContainer.scrollWidth - scrollContainer.clientWidth;

    if (maxScrollLeft <= 0) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const activeRect = activeLink.getBoundingClientRect();

    const targetLeft =
      scrollContainer.scrollLeft +
      activeRect.left -
      containerRect.left -
      (scrollContainer.clientWidth - activeRect.width) / 2;

    scrollContainer.scrollTo({
      left: Math.max(0, Math.min(targetLeft, maxScrollLeft)),
      behavior: 'auto',
    });
  };
  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    requestAnimationFrame(this.#centerActiveCollectionLink);
    if (!this.hasAttribute('sticky')) return;

    this.#scrollMarker = document.createElement('span');
    this.#scrollMarker.setAttribute('aria-hidden', 'true');
    this.#scrollMarker.style.cssText = 'display:block;flex:none;block-size:0;inline-size:0;pointer-events:none';
    (this.closest('.header-section') ?? this).before(this.#scrollMarker);
    this.#handleBreakpointChange();
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    this.#productColumnsMediaQuery.addEventListener('change', this.#resetScrollState);
    this.#productTouchMediaQuery.addEventListener('change', this.#resetScrollState);
    document.addEventListener('shopify:section:load', this.#resetScrollState);
    document.addEventListener('shopify:section:unload', this.#resetScrollState);
    window.addEventListener('resize', this.#measureScrollOrigin, { passive: true });
    window.addEventListener('pageshow', this.#resetScrollState);
    document.addEventListener('theme:scroll-restored', this.#resetScrollState);
    document.addEventListener('theme:header-resized', this.#measureScrollOrigin);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#productColumnsMediaQuery.removeEventListener('change', this.#resetScrollState);
    this.#productTouchMediaQuery.removeEventListener('change', this.#resetScrollState);
    document.removeEventListener('shopify:section:load', this.#resetScrollState);
    document.removeEventListener('shopify:section:unload', this.#resetScrollState);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    window.removeEventListener('resize', this.#measureScrollOrigin);
    window.removeEventListener('pageshow', this.#resetScrollState);
    document.removeEventListener('theme:scroll-restored', this.#resetScrollState);
    document.removeEventListener('theme:header-resized', this.#measureScrollOrigin);
    this.#scrollMarker?.remove();
    this.#scrollMarker = null;
    if (this.#scrollRafId !== null) cancelAnimationFrame(this.#scrollRafId);
    this.#scrollRafId = null;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const headerGroup = document.querySelector('#header-group');
  if (!headerGroup) return;
  const updateHeight = () => {
    const header = headerGroup.querySelector('header-component');
    const value = `${Math.round(calculateHeaderGroupHeight(header, headerGroup))}px`;
    if (document.body.style.getPropertyValue('--header-group-height') !== value) {
      document.body.style.setProperty('--header-group-height', value);
      document.dispatchEvent(new Event('theme:header-resized'));
    }
  };
  const resizeObserver = new ResizeObserver(updateHeight);
  const observeChildren = () => {
    resizeObserver.disconnect();
    for (const element of headerGroup.children) {
      if (element instanceof HTMLElement) resizeObserver.observe(element);
    }
    updateHeight();
  };
  const mutationObserver = new MutationObserver(observeChildren);
  mutationObserver.observe(headerGroup, { childList: true });
  observeChildren();
});
